import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  Bookmark,
  CalendarDays,
  Clock3,
  Plus,
  X,
} from "lucide-react";
import BookmarkShapeCard from "./GroupCard";
import {
  ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
  isElectronicBookmarkGroupNameTooLong,
} from "../api/editGropus";
import {
  MODAL_ANIMATION_CSS,
  MODAL_OVERLAY_STYLE,
  MODAL_PANEL_STYLE,
  useBodyScrollLock,
} from "./modalEffects";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getAdaptiveActionButtonStyle,
} from "../utils/actionButtonStyles";
import { changeBookNums } from "../utils/helpers";

type BookOpenGroupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  bookTitle: string | null;
  bookSubtitle?: string | null;
  bookCoverSrc?: string | null;
  primaryColor?: string;
  isAdultBook: boolean;
  bookmarks: ElectronicBookmark[];
  onOpenBookmark: (bookmark: ElectronicBookmark) => void;
  onCreateGroup: (groupName: string) => Promise<void> | void;
  isCreatingGroup: boolean;
  onClearMessage: () => void;
  bookmarkMessage: {
    type: "success" | "error" | "info" | "warning";
    text: string;
  } | null;
};

const normalizeGroupName = (groupName: string) =>
  groupName.trim().replace(/\s+/g, " ").toLowerCase();

const formatBookmarkDate = (timestamp: number) =>
  new Date(timestamp).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatBookmarkTime = (timestamp: number) =>
  new Date(timestamp).toLocaleString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  });

const BookOpenGroupModal: React.FC<BookOpenGroupModalProps> = ({
  isOpen,
  onClose,
  bookTitle,
  bookSubtitle,
  bookCoverSrc,
  primaryColor = "#f6851f",
  isAdultBook,
  bookmarks,
  onOpenBookmark,
  onCreateGroup,
  isCreatingGroup,
  onClearMessage,
  bookmarkMessage,
}) => {
  const [groupNameDraft, setGroupNameDraft] = useState("");
  const [localValidationMessage, setLocalValidationMessage] = useState<{
    type: "success" | "error" | "info" | "warning";
    text: string;
  } | null>(null);
  const [highlightedGroupName, setHighlightedGroupName] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!isOpen) {
      setGroupNameDraft("");
      setLocalValidationMessage(null);
      setHighlightedGroupName(null);
    }
  }, [isOpen]);

  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const isGroupNameTooLong =
    isElectronicBookmarkGroupNameTooLong(groupNameDraft);
  const displayedMessage = localValidationMessage ?? bookmarkMessage;
  const bookmarkGridColumnsClass = "grid-cols-2";

  const handleCreateGroup = async () => {
    const cleanedGroupName = groupNameDraft.trim().replace(/\s+/g, " ");
    if (!cleanedGroupName) return;
    if (isGroupNameTooLong) {
      setLocalValidationMessage({
        type: "error",
        text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
      });
      return;
    }

    try {
      await onCreateGroup(cleanedGroupName);
      setHighlightedGroupName(normalizeGroupName(cleanedGroupName));
      setGroupNameDraft("");
      setLocalValidationMessage(null);
    } catch {
      return;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[105] flex items-start justify-center overflow-y-auto bg-slate-950/55 px-4 py-4 backdrop-blur-sm sm:items-center sm:py-6"
      style={MODAL_OVERLAY_STYLE}
    >
      <style>{MODAL_ANIMATION_CSS}</style>
      <div
        className="relative my-auto flex max-h-[calc(100dvh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/35 bg-[linear-gradient(180deg,rgba(255,252,248,0.98),rgba(248,250,252,0.98))] shadow-[0_40px_120px_rgba(15,23,42,0.24)] sm:max-h-[92vh]"
        style={MODAL_PANEL_STYLE}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/90 text-slate-700 shadow-sm transition hover:bg-white"
          aria-label="Zamknij"
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>

        <div className="grid min-h-0 flex-1 gap-0 overflow-y-auto lg:grid-cols-[280px,minmax(0,1fr)] lg:overflow-hidden">
          <div className="border-b border-slate-200/80 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,248,240,0.9))] px-5 py-6 sm:px-8 lg:border-b-0 lg:border-r lg:px-7 lg:py-8">
            <div className="max-w-sm pr-16 sm:pr-0">
              <div className="flex items-start gap-4 sm:block">
                <div className="min-w-0 flex-1">
                  <div
                    className="hidden h-14 w-14 items-center justify-center rounded-[18px] border border-white/60 bg-white/80 shadow-[0_18px_40px_rgba(148,163,184,0.16)] sm:flex"
                    style={{ color: primaryColor }}
                  >
                    <Bookmark size={24} strokeWidth={2.1} aria-hidden="true" />
                  </div>

                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 sm:mt-5">
                    Zakładki do:
                  </p>
                  <h2 className="mt-2 text-base font-semibold leading-tight tracking-[-0.04em] text-slate-950 sm:text-[1.3rem]">
                    {bookTitle ?? "Podręcznik"}
                  </h2>
                  <p className="mt-1 text-sm italic leading-5 text-slate-500 sm:mt-0 sm:text-2xl">
                    {bookSubtitle}
                  </p>
                </div>

                {bookCoverSrc ? (
                  <div className="aspect-square w-20 shrink-0 overflow-hidden rounded-[18px] border border-white/60 bg-white/75 shadow-[0_20px_40px_rgba(148,163,184,0.12)] sm:mt-6 sm:aspect-auto sm:h-auto sm:w-full sm:rounded-[24px]">
                    <img
                      src={bookCoverSrc}
                      alt={bookTitle ?? "Okładka podręcznika"}
                      className="h-full w-full object-cover sm:h-auto"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="min-h-0 flex flex-1 flex-col p-5 sm:p-6 lg:px-8 lg:py-8">
            <p className="mb-4 hidden text-sm font-semibold uppercase tracking-[0.22em] text-slate-500 sm:block">
              Wybierz grupę
            </p>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div
                className={`grid ${bookmarkGridColumnsClass} gap-3 pr-1 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3`}
              >
                {bookmarks.map((bookmark) => {
                  const isActive =
                    normalizeGroupName(bookmark.groupName) ===
                    highlightedGroupName;
                  const savedPageNum =
                    typeof bookmark.pageNum === "number"
                      ? bookmark.pageNum
                      : null;
                  const hasSavedBookmark = savedPageNum !== null;
                  const pageLabel = hasSavedBookmark
                    ? isAdultBook
                      ? changeBookNums(savedPageNum)
                      : savedPageNum
                    : null;

                  return (
                    <BookmarkShapeCard
                      key={bookmark.id}
                      title={bookmark.groupName}
                      badge={`strona ${pageLabel ?? 1}`}
                      accentColor={primaryColor}
                      isBadgeMuted={!hasSavedBookmark}
                      hoverLabel={`${pageLabel ?? 1}`}
                      tintBackgroundOnMobile
                      disableHoverOnMobile
                      footer={
                        hasSavedBookmark ? (
                          <div className="flex items-center justify-center gap-3 sm:justify-between">
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarDays
                                size={14}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                              <span>
                                {formatBookmarkDate(bookmark.updatedAt)}
                              </span>
                            </span>
                            <span className="hidden shrink-0 items-center gap-1.5 text-right sm:inline-flex">
                              <Clock3
                                size={14}
                                strokeWidth={2}
                                aria-hidden="true"
                              />
                              <span>
                                {formatBookmarkTime(bookmark.updatedAt)}
                              </span>
                            </span>
                          </div>
                        ) : (
                          <div className=" text-slate-500">
                            Nie dodano zakładki
                          </div>
                        )
                      }
                      isActive={isActive}
                      onClick={() => onOpenBookmark(bookmark)}
                    />
                  );
                })}
              </div>
            </div>

            <div className="mt-5">
              {displayedMessage && (
                <div
                  className={`mb-4 rounded-2xl border px-4 py-3 text-sm ${
                    displayedMessage.type === "error"
                      ? "border-red-200 bg-red-50 text-red-700"
                      : displayedMessage.type === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : displayedMessage.type === "warning"
                          ? "border-amber-300 bg-amber-50 text-amber-900 shadow-[0_12px_30px_rgba(245,158,11,0.12)]"
                          : "border-slate-200 bg-slate-100 text-slate-700"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {displayedMessage.type === "warning" ? (
                      <AlertTriangle
                        size={18}
                        strokeWidth={2.3}
                        className="mt-0.5 shrink-0 text-amber-600"
                        aria-hidden="true"
                      />
                    ) : null}
                    <span>{displayedMessage.text}</span>
                  </div>
                </div>
              )}

              <div className="border-t border-slate-200/80 pt-5">
                <div className="flex w-full items-center gap-3 rounded-[22px]">
                  <input
                    type="text"
                    value={groupNameDraft}
                    onChange={(e) => {
                      setGroupNameDraft(e.target.value);
                      setLocalValidationMessage(null);
                      if (bookmarkMessage?.type === "warning") {
                        onClearMessage();
                      }
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();

                      if (e.key === "Enter") {
                        e.preventDefault();
                        void handleCreateGroup();
                      }
                    }}
                    placeholder="Nadaj nazwę, by utworzyć grupę"
                    className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base outline-none transition placeholder:text-xs focus:border-[#f6851f] focus:ring-4 focus:ring-[#f6851f]/10 sm:placeholder:text-sm ${
                      isGroupNameTooLong ? "text-red-600" : "text-slate-900"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => void handleCreateGroup()}
                    disabled={isCreatingGroup || !groupNameDraft.trim()}
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                    style={getAdaptiveActionButtonStyle(primaryColor)}
                    aria-label="Dodaj grupę"
                  >
                    <Plus size={18} strokeWidth={2.5} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookOpenGroupModal;
